import { useState, useCallback } from 'react';

// Contextes possibles pour l'onboarding du swipe
export type SwipeOnboardingContext = 'card' | 'modal';

// Clés de stockage séparées pour cards et modales
const STORAGE_KEYS: Record<SwipeOnboardingContext, string> = {
  card: 'w2d_swipe_onboarding_card_done',
  modal: 'w2d_swipe_onboarding_modal_done',
};

/**
 * Hook de gestion de l'onboarding du swipe
 * Gère l'affichage unique de l'animation wiggle et du hint textuel
 *
 * @param context - 'card' pour les EventCards, 'modal' pour les EventModals
 *                  Permet de montrer l'animation une fois par type de composant
 */
export interface SwipeOnboarding {
  /** L'utilisateur a-t-il déjà vu le hint de swipe pour ce contexte ? */
  hasSeenSwipeHint: boolean;
  /** Marquer le hint comme vu (persiste en localStorage) */
  markSwipeHintSeen: () => void;
  /** Doit-on afficher l'animation wiggle ? */
  shouldShowWiggle: boolean;
}

export const useSwipeOnboarding = (context: SwipeOnboardingContext = 'card'): SwipeOnboarding => {
  const storageKey = STORAGE_KEYS[context];

  // Lecture synchrone du localStorage pour éviter le flash
  const [hasSeenSwipeHint, setHasSeenSwipeHint] = useState<boolean>(() => {
    try {
      return localStorage.getItem(storageKey) === 'true';
    } catch {
      // Fallback si localStorage indisponible (mode privé, etc.)
      return false;
    }
  });

  const markSwipeHintSeen = useCallback(() => {
    try {
      localStorage.setItem(storageKey, 'true');
    } catch {
      // Silently fail si localStorage indisponible
    }
    setHasSeenSwipeHint(true);
  }, [storageKey]);

  return {
    hasSeenSwipeHint,
    markSwipeHintSeen,
    shouldShowWiggle: !hasSeenSwipeHint,
  };
};

export default useSwipeOnboarding;
